import React, { FC } from 'react';
import { Icon } from 'src/components/widgets/icon';
import PlusIcon from 'src/shared/material_icons/content/add.svg';
import styled from 'styled-components';
import { Link, PrimaryButton } from '../buttons';
import { SectionContainer } from '../section/sectionContainer';
import { T } from '../helpers/helpers';
import EmptyListIcon from '../icons/emptyListIcon.svg';

const TopIcon = styled.div`
    margin: 20px 0px 20px 0 !important;
    transform: scale(0.8);
    transform-origin: center center;
`;

const EmptyListContainer = styled.div<{ $inCard?: boolean }>`
    display: grid;
    justify-items: center;
    padding-top: ${(props) => (props.$inCard ? '0' : '60px')};
    color: #263238;
`;

// The page card an empty state sits in when it is the page's own content, rather than a "no results"
// message inside a list, a dialog or a section that already draws its own surface. The card's padding
// replaces the container's head room, so the drawing stays centred.
const EmptyStateCard = styled(SectionContainer)`
    padding: 56px 24px 64px;
`;
const Header = styled.h3`
    font-size: 14px;
    font-weight: 600;
    margin: 0;
    padding-bottom: 8px;
    color: #263238;
`;

const SubHeader = styled.span`
    padding-bottom: 5px;
    white-space: pre-line;
    line-height: 20px;
    text-align: center;
    color: #263238;
    font-size: 13px;
`;

const LearnMoreContainer = styled.div`
    font-size: 13px;
`;

const Buttons = styled.div<{ $hasNote?: boolean; $compact?: boolean }>`
    display: flex;
    gap: 30px;
    justify-content: center;
    align-items: center;
    flex-wrap: wrap;
    margin-top: ${(props) => (props.$hasNote ? '14px' : props.$compact ? '10px' : '30px')};
`;

const Footer = styled.div`
    margin-top: 24px;
    font-size: 12px;
    color: #263238;
`;

// A muted helper line shown right above the buttons (e.g. "To do X, you need a product first").
const Hint = styled.div`
    margin-top: 24px;
    font-size: 13px;
    color: #90a4ae;
    text-align: center;
`;

// Optional helper line shown right above the action buttons (e.g. "you need a product first"). Kept close to
// the buttons: larger gap above to separate it from the description, small gap below to the buttons.
const Note = styled.div`
    margin-top: 28px;
    color: #6b7280;
    font-size: 13px;
    text-align: center;
`;

interface Props {
    className?: string;
    title: string;
    subTitle: React.ReactNode;
    buttonTitle?: string;
    onButtonClick?: () => void;
    alreadyExistingButton?: React.ReactNode;
    icon?: React.ReactNode;
    hideIcon?: boolean;
    learnMoreText?: string;
    learnMoreLink?: string;
    onLearnMoreClick?: () => void;
    disabledButton?: boolean;
    // Rendered right above the buttons, e.g. "To create templates, you need a product first".
    hint?: React.ReactNode;
    // Rendered under the buttons, e.g. a secondary "load demo data" prompt.
    footer?: React.ReactNode;
    // Helper line rendered just above the action buttons.
    note?: React.ReactNode;
    // Wraps the empty state in the standard page card. Turn it on when the empty state is the page's
    // own content; leave it off inside dialogs, lists and sections that already draw a surface.
    card?: boolean;
}

export const EmptyList: FC<Props> = ({
    className,
    title,
    subTitle,
    buttonTitle,
    onButtonClick,
    alreadyExistingButton,
    learnMoreText,
    learnMoreLink,
    onLearnMoreClick,
    icon: iconLink,
    hideIcon = false,
    disabledButton = false,
    hint,
    footer,
    note,
    card = false
}) => {
    const handleLearnMoreClick = onLearnMoreClick
        ? (e: React.MouseEvent) => {
              e.preventDefault();
              onLearnMoreClick();
          }
        : undefined;

    const content = (
        <EmptyListContainer className={className} $inCard={card}>
            {!hideIcon &&
                (iconLink ? (
                    <TopIcon>{iconLink}</TopIcon>
                ) : (
                    <TopIcon>
                        <EmptyListIcon />
                    </TopIcon>
                ))}
            <Header>{title}</Header>
            <SubHeader>{subTitle}</SubHeader>
            {learnMoreLink && (
                <LearnMoreContainer>
                    <Link href={learnMoreLink} target={'_blank'} onClick={handleLearnMoreClick}>
                        {learnMoreText ? learnMoreText : T._('Learn more', 'Admin')}
                    </Link>
                </LearnMoreContainer>
            )}

            {note ? <Note>{note}</Note> : hint ? <Hint>{hint}</Hint> : null}

            <Buttons $hasNote={!!note} $compact={!!hint}>
                {buttonTitle && onButtonClick && (
                    <PrimaryButton disabled={disabledButton} onClick={() => onButtonClick()}>
                        <Icon>
                            <PlusIcon />
                        </Icon>
                        {buttonTitle}
                    </PrimaryButton>
                )}
                {alreadyExistingButton}
            </Buttons>
            {footer && <Footer>{footer}</Footer>}
        </EmptyListContainer>
    );

    return card ? <EmptyStateCard>{content}</EmptyStateCard> : content;
};
